// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';

// Import your new component
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

// Core Providers & Layouts
import { AuthProvider } from './contexts/AuthContext';
import { FacilityProvider } from './contexts/FacilityContext';
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AccountLayout from './layouts/AccountLayout';

// Page Components (Import all needed pages)
import HomePage from './pages/HomePage/HomePage';
import LandingPage from './pages/LandingPage/LandingPage';
import LoginPage from './pages/LoginPage/LoginPage';
import RegisterPage from './pages/RegisterPage/RegisterPage';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminUserManagement from './pages/Admin/AdminUserManagement';
import AdminReservationManagementPage from './pages/Admin/AdminReservationManagementPage';
import AdminDocumentManagementPage from './pages/Admin/AdminDocumentManagementPage';
import AdminCalendarSettings from './pages/Admin/AdminCalendarSettings';
import AdminClosureManagementPage from './pages/Admin/AdminClosureManagementPage';
import AboutPage from './pages/AboutPage/AboutPage';
import ContactPage from './pages/ContactPage/ContactPage';
import GalleryPage from './pages/GalleryPage/GalleryPage';
import PublicReservationPage from './pages/PublicReservationPage/PublicReservationPage';

// Updated imports for Facilities page and its tab content
import FacilitiesPage from './pages/FacilitiesPage/FacilitiesPage';
import FloorPlansContent from './pages/FacilitiesPage/FloorPlansContent.jsx';
// No need to import Basement, First, Second floor components separately anymore
import OutdoorAreasContent from './pages/FacilitiesPage/OutdoorAreasContent.jsx';
import KitchenTabContent from './pages/FacilitiesPage/KitchenTabContent.jsx';

import AccountProfilePage from './pages/Account/AccountProfilePage';
import MyReservationsPage from './pages/Account/MyReservationsPage';
import AccountPaymentsPage from './pages/Account/AccountPaymentsPage';
import AccountDocumentationPage from './pages/Account/AccountDocumentationPage';
import AccountHistoryPage from './pages/Account/AccountHistoryPage';

// Import new password recovery pages
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';

// Simple NotFound component
const NotFoundPage = () => (
  <div style={{ paddingTop: '100px', paddingBottom: '100px', textAlign: 'center', minHeight: 'calc(100vh - 200px)' }}>
    <h2>404 - Page Not Found</h2>
    <p>Sorry, the page you are looking for does not exist.</p>
    <Link to="/home">Go to Homepage</Link>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop /> {/* Ensure this is at the top to handle scroll restoration */}
      <AuthProvider>
        <FacilityProvider>
          <Routes>
            {/* Landing Page (Splash Screen) - Standalone */}
            <Route path="/" element={<LandingPage />} />

            {/* Public routes using MainLayout */}
            <Route element={<MainLayout />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/request-reservation" element={<PublicReservationPage />} />

              <Route path="/facilities" element={<FacilitiesPage />}>
                {/* The "Floor Plans" tab will be the default. Its component handles the sub-navigation internally. */}
                <Route index element={<FloorPlansContent />} />
                <Route path="floor-plans" element={<FloorPlansContent />} />

                {/* The other main tabs */}
                <Route path="outdoor-areas" element={<OutdoorAreasContent />} />
                <Route path="kitchen" element={<KitchenTabContent />} />
              </Route>

            </Route>

            {/* Standalone routes (no MainLayout or AccountLayout) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected User Account Routes - Nested under /account path */}
            <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="reservations" replace />} />
              <Route path="profile" element={<AccountProfilePage />} />
              <Route path="reservations" element={<MyReservationsPage />} />
              <Route path="payments" element={<AccountPaymentsPage />} />
              <Route path="documentation" element={<AccountDocumentationPage />} />
              <Route path="history" element={<AccountHistoryPage />} />
            </Route>

            {/* Protected Admin Routes - Nested under /admin path */}
            <Route path="/admin" element={<ProtectedRoute isAdminRoute={true}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUserManagement />} />
              <Route path="reservations" element={<AdminReservationManagementPage />} />
              <Route path="documents" element={<AdminDocumentManagementPage />} />
              <Route path="calendar-settings" element={<AdminCalendarSettings />} />
              <Route path="closures" element={<AdminClosureManagementPage />} />
            </Route>

            {/* General Fallback 404 - Ensure this is last */}
            <Route path="*" element={<MainLayout><NotFoundPage /></MainLayout>} />

          </Routes>
        </FacilityProvider>
      </AuthProvider>
    </Router>
  );
}
export default App;